import React, { useState, useEffect } from "react";
import {
  Input,
  Row,
  Typography,
  Col,
  Card,
  Table,
  Spin,
  Tag,
  Select,
  Button,
  Space,
  Form,
} from "antd";
import {
  SearchOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/layouts/MainLayout";
import { BOM } from "../../../services/definedMethod";
import cnt from "../../../services/definedConstant";
import { connect, useDispatch } from "react-redux";
import TopContent from "../../../components/layouts/TopContent";
import { rightAction } from "../../../redux/actions";
import DownloadTableButtons from "../../../components/DownloadTableButtons";
import { apiConfig, labelConfig, staticConfig } from "../customer.config";
import StatusSelect from "../../../components/CustomStatusSelect";
import CustomPagination from "../../../components/CustomPagination";

// ── The only new import ───────────────────────────────────────────────────────
import PdfDownloadButton from "../../../components/CustomPdfDownloader";

const { Text } = Typography;

// ── PDF column definitions (outside component — no re-creation on render) ─────
const CUSTOMER_PDF_COLUMNS = [
  { header: "#", key: null, width: 10, type: "index" },
  { header: "Client ID", key: "clientno", width: 28 },
  { header: "Customer Name", key: "name", width: 52 },
  { header: "Mobile No.", key: "mobileNo", width: 36 },
  { header: "Branch", key: "branch", width: 44 },
  { header: "Relationship Manager", key: "staff", width: 44 },
  { header: "Status", key: "status", width: 26, type: "status" },
];

const ManageCustomer = (props) => {
  const navigate = useNavigate();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData;

  const [customerList, setCustomerList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [originalList, setOriginalList] = useState([]);
  const [tableCount, setTableCount] = useState("10");
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("active");
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearch, setIsSearch] = useState(false);

  const dispatch = useDispatch();
  const [useForm] = Form.useForm();

  const columns = [
    {
      title: labelConfig.clientId,
      dataIndex: "clientno",
      sorter: (a, b) => a.clientno.localeCompare(b.clientno),
    },
    {
      title: labelConfig.name,
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    { title: labelConfig.mobilePhone, dataIndex: "mobileNo" },
    {
      title: labelConfig.branch,
      dataIndex: "branch",
      sorter: (a, b) => a.branch.localeCompare(b.branch),
    },
    { title: labelConfig.relationshipManager, dataIndex: "staff" },
    {
      title: labelConfig.status,
      dataIndex: "status",
      render: (_, record) => (
        <Text>{handleFlgStatus(record?.status, {}, true)}</Text>
      ),
    },
  ];

  useEffect(() => {
    BOM.HasPermission("READ_CLIENT") && getCustomerListAction();
    BOM.HasPermission("CREATE_CLIENT") && rightAction(cnt.RIGHT_ADD_BTN);
  }, [selectedStatus]);

  function mapItems(items = []) {
    return items.map((el) => {
      const sv = el?.status?.value ?? "-";
      return {
        key: el?.id,
        id: el?.id,
        name: el?.displayName ?? "-",
        clientno: el?.accountNo ?? "-",
        mobileNo: el?.mobileNo ?? "-",
        status:
          sv.toLowerCase() === "submitted and pending approval"
            ? "Pending"
            : sv,
        branch: el?.officeName ?? "-",
        staff: el?.staffName ?? "-",
        branchId: el?.officeId ?? "-",
      };
    });
  }

  const getCustomerListAction = (offset = 0, limit = Number(tableCount)) => {
    setCurrentPage(1);
    setIsLoading(true);
    setIsSearch(false);
    BOM.FetchReqAction(
      {
        session,
        params: {
          limit,
          offset,
          orderBy: "id",
          ...(selectedStatus !== "status" && { status: selectedStatus }),
        },
      },
      apiConfig.Clients,
      (err, res) => {
        setIsLoading(false);
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
          return;
        }
        setTotalItems(res?.totalFilteredRecords);
        const mapped = mapItems(res.pageItems);
        setCustomerList(mapped);
        setOriginalList(mapped);
      },
    );
  };

  // Existing CSV fetcher — unchanged
  const getCsvListData = () =>
    new Promise((resolve, reject) => {
      BOM.FetchReqAction(
        { session, params: { limit: 1000, offset: 0, orderBy: "id" } },
        apiConfig.Clients,
        (err, res) => (err ? reject(err) : resolve(mapItems(res.pageItems))),
      );
    });

  // PDF data fetcher — passed to PdfDownloadButton, same pattern as CSV
  const getPdfData = () =>
    new Promise((resolve, reject) => {
      BOM.FetchReqAction(
        { session, params: { limit: 1000, offset: 0, orderBy: "id" } },
        apiConfig.Clients,
        (err, res) => (err ? reject(err) : resolve(mapItems(res.pageItems))),
      );
    });

  const onSelectItemAction = (selectedRowKeys, objParam) => {
    setSelectedRow(objParam[0]);
    [
      BOM.HasPermission("READ_CLIENT") && cnt.RIGHT_DETAIL_BTN,
      BOM.HasPermission("UPDATE_CLIENT") && cnt.RIGHT_EDIT_BTN,
    ].forEach((item) => item && dispatch({ type: item }));
  };

  function selectTableCount(value) {
    setTableCount(value);
    onRefresh(0, value);
  }
  function handleOnView() {
    if (rightBtn.detailBtn?.toLowerCase() === "primary")
      navigate("/detail-customer", { state: selectedRow });
  }
  function handleOnCreate() {
    if (rightBtn.addBtn?.toLowerCase() === "primary")
      navigate("/new-customer", { state: selectedRow });
  }
  function handleOnEdit() {
    if (rightBtn.editBtn?.toLowerCase() === "primary")
      navigate("/edit-customer", { state: selectedRow });
  }

  const onSearch = (formValue) => {
    setIsLoading(true);
    setIsSearch(true);
    BOM.FetchReqAction(
      {
        session,
        params: {
          exactMatch: false,
          query: formValue.searchKey,
          resource: "clients,clientIdentifiers",
        },
      },
      apiConfig.Search,
      (err, res) => {
        setIsLoading(false);
        if (err) {
          BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
          return;
        }
        const mapped = (res || []).map((el) => {
          const sv = el?.entityStatus?.value ?? "-";
          return {
            key: el?.entityId,
            id: el?.entityId,
            name: el?.entityName ?? "-",
            clientno: el?.entityAccountNo ?? "-",
            mobileNo: el?.entityMobileNo ?? "-",
            status:
              sv.toLowerCase() === "submitted and pending approval"
                ? "Pending"
                : sv,
            branch: el?.parentName ?? "-",
            branchId: el?.parentId ?? "-",
            staff: el?.staffName ?? "-",
          };
        });
        setCustomerList(mapped);
        setOriginalList(mapped);
      },
    );
  };

  const onRefresh = (offset = 0, limit = Number(tableCount)) => {
    useForm.resetFields();
    getCustomerListAction(offset, limit);
    setCurrentPage(1);
  };

  const handleFlgStatus = (status, style = {}, isListing = false) => {
    switch (status?.toLowerCase()) {
      case "active":
        return (
          <Tag
            icon={<CheckCircleOutlined />}
            color={isListing ? "success" : "#87D068"}
            style={style}
          >
            {status}
          </Tag>
        );
      case "closed":
        return (
          <Tag
            icon={<CloseCircleOutlined />}
            color={isListing ? "red" : "#FA0008"}
            style={style}
          >
            {status}
          </Tag>
        );
      default:
        return (
          <Tag
            icon={<ClockCircleOutlined />}
            color={isListing ? "warning" : "#FFEB86"}
            style={{ color: "black", ...style }}
          >
            {BOM.SubstringFormat(status, 21)}
          </Tag>
        );
    }
  };

  return (
    <MainLayout
      isEnableRightSidebar
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
    >
      <TopContent
        pageTitle={labelConfig.manageCustomer}
        pageItems={staticConfig.manageBreadcrumbItems}
      />

      <Card
        title={
          <>
            Customer List{" "}
            <Button
              type="primary"
              shape="round"
              size="small"
              className="of-primaryBtnColor"
              onClick={() => onRefresh()}
              icon={<ReloadOutlined />}
              loading={isLoading}
            >
              {labelConfig.refresh}
            </Button>
          </>
        }
        // extra={
        //   BOM.HasPermission("READ_CLIENT") && (
        //     // ── PdfDownloadButton — drop it, pass 4 props, done ─────────────
        //     <PdfDownloadButton
        //       title="Customer Listing Report"
        //       subtitle={`Status: ${selectedStatus === "status" ? "All" : selectedStatus}`}
        //       fileName="Customer_List"
        //       fetchData={getPdfData}
        //       columns={CUSTOMER_PDF_COLUMNS}
        //       summary={[
        //         { label: "Total Records", value: totalItems },
        //         {
        //           label: "Active",
        //           value: customerList.filter(
        //             (r) => r.status?.toLowerCase() === "active",
        //           ).length,
        //         },
        //         {
        //           label: "Closed",
        //           value: customerList.filter(
        //             (r) => r.status?.toLowerCase() === "closed",
        //           ).length,
        //         },
        //         {
        //           label: "Pending",
        //           value: customerList.filter(
        //             (r) => r.status?.toLowerCase() === "pending",
        //           ).length,
        //         },
        //       ]}
        //     />
        //   )
        // }
        style={{
          boxShadow: "0px 0px 30px 3px rgba(0,0,0,0.1)",
          borderRadius: 20,
        }}
      >
        <Spin spinning={isLoading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <div style={{ marginBottom: "auto" }}>
              <Text>Show</Text>
              <Select
                defaultValue={tableCount}
                options={[
                  { value: "5", label: "5" },
                  { value: "10", label: "10" },
                  { value: "25", label: "25" },
                  { value: "50", label: "50" },
                ]}
                onSelect={selectTableCount}
              />
              <Text>Entries</Text>
            </div>
            <Row style={{ marginLeft: 10 }}>
              <Col span={24} style={{ padding: "0px 8px" }}>
                <Form form={useForm} onFinish={onSearch}>
                  <Space.Compact style={{ width: "100%" }}>
                    <Form.Item
                      name="searchKey"
                      rules={[
                        {
                          required: true,
                          message: BOM.CheckReqFld("This field "),
                        },
                      ]}
                    >
                      <Input
                        placeholder="Search by Client: Name / ID / Mobile Number"
                        style={{ width: 400 }}
                        allowClear
                      />
                    </Form.Item>
                    <Button
                      type="primary"
                      className="of-primaryBtnColor"
                      htmlType="submit"
                      style={{ borderRadius: "0px 5px 5px 0px" }}
                    >
                      <SearchOutlined />
                    </Button>
                    <div style={{ marginLeft: "20px" }}>
                      <div style={{ marginBottom: "auto", marginLeft: 10 }}>
                        <Select
                          value={selectedStatus}
                          options={staticConfig.customerStatusOptions}
                          onChange={setSelectedStatus}
                          placeholder="Select Status"
                          dropdownStyle={{ width: "250px" }}
                          className="interbank-table-select"
                        />
                      </div>
                    </div>
                  </Space.Compact>
                </Form>
              </Col>
            </Row>
            <DownloadTableButtons
              downloadData={customerList}
              columns={columns}
              fileName="Customer List"
              onExportClick={getCsvListData}
            />
          </Row>

          <Table
            rowSelection={{ type: "radio", onChange: onSelectItemAction }}
            columns={columns}
            dataSource={customerList}
            bordered
            pagination={false}
            scroll={{ x: 800 }}
          />

          {!isSearch && (
            <CustomPagination
              totalItems={totalItems}
              pageSize={Number(tableCount)}
              onPageChange={(page, offset) => onRefresh(offset)}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});
const mapDispatchToProps = { rightAction };
export default connect(mapStateToProps, mapDispatchToProps)(ManageCustomer);
